/**
 * useActiveSlate.ts
 * 
 * SINGLE DATA SOURCE for GM Roster Builder
 * 
 * Per Architect Decision (LOCKED):
 * - This hook OWNS all slate data fetching
 * - GMRosterBuilderV2 calls this hook
 * - Hub does NOT fetch slate data
 * 
 * Provides:
 * - players: Normalized player array
 * - playersForSlot(label): Position-filtered players for a slot
 * - pool: Pool configuration
 * - loading/error states
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { normalizePlayer } from '../utils/transformers';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// Slot position mapping
const SLOT_POSITIONS: Record<string, string[]> = {
  'QB': ['QB'],
  'RB1': ['RB'],
  'RB2': ['RB'],
  'WR1': ['WR'],
  'WR2': ['WR'],
  'TE': ['TE'],
  'FLEX': ['RB', 'WR', 'TE'],
};

interface SlateData {
  pool: any;
  players?: any[];
  player_props?: any[];
  teams?: any;
}

interface NormalizedPlayer {
  id: number;
  name: string;
  team: string;
  position: string;
  pos: string;
  opp: string;
  passDefRank: number;
  runDefRank: number;
  edge: number;
  edgePct: number;
  props: any[];
}

/**
 * useActiveSlate - Single source of truth for slate data
 * 
 * @param poolId - Pool ID (number or string)
 * @returns { players, playersForSlot, pool, loading, error }
 */
export function useActiveSlate(poolId: number | string | null) {
  const [slate, setSlate] = useState<SlateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch slate data
  useEffect(() => {
    if (!poolId) {
      setSlate(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const fetchSlate = async () => {
      try {
        // Use string pool_id directly - backend accepts string (Phase 3H-U fix)
        const slateRes = await fetch(`${API_BASE}/pools/${poolId}/slate`);
        if (!slateRes.ok) throw new Error(`HTTP ${slateRes.status}`);
        const data = await slateRes.json();
        setSlate(data);
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        setLoading(false);
      }
    };

    fetchSlate();
  }, [poolId]);

  // Transform slate to normalized players
  const players: NormalizedPlayer[] = useMemo(() => {
    if (!slate) return [];
    
    // Handle both `players` and `player_props` response formats
    const rawPlayers = slate.players || slate.player_props || [];
    
    // Group props by player if using player_props format
    if (slate.player_props && !slate.players) {
      const playerMap = new Map<number, any>();
      
      for (const prop of slate.player_props) {
        const playerId = prop.player_id;
        if (!playerMap.has(playerId)) {
          playerMap.set(playerId, {
            id: playerId,
            name: prop.player_name,
            team: prop.team,
            position: prop.position,
            pos: prop.position,
            opp: prop.event_name?.split(' @ ')[1]?.split(' ')[0] || prop.opponent || 'TBD',
            opponent: prop.event_name?.split(' @ ')[1]?.split(' ')[0] || prop.opponent || 'TBD',
            opponentDefenseRank: prop.opponent_defense_rank || 16,
            props: []
          });
        }
        
        playerMap.get(playerId)!.props.push({
          id: prop.id,
          stat: prop.stat_type,
          statType: prop.stat_type,
          line: prop.line_value,
          lineValue: prop.line_value,
          playerSeasonAvg: prop.playerSeasonAvg ?? prop.playerAvg ?? prop.player_avg ?? 0,
          avg: prop.playerSeasonAvg ?? prop.playerAvg ?? prop.player_avg ?? 0,
          edge: prop.edgeValue ?? prop.edge ?? 0,
          edgeValue: prop.edgeValue ?? prop.edge ?? 0,
          overOdds: prop.over_odds || -110,
          underOdds: prop.under_odds || -110,
        });
      }
      
      return Array.from(playerMap.values()).map(normalizePlayer);
    }
    
    return rawPlayers.map(normalizePlayer);
  }, [slate]);

  // Extract pool config
  const pool = useMemo(() => {
    if (!slate?.pool) return null;
    return {
      id: slate.pool.id,
      name: slate.pool.name || slate.pool.pool_name,
      slots: slate.pool.slots || 7,
      units: slate.pool.units || 100,
      deadline: slate.pool.deadline,
      kickoff: slate.pool.kickoff,
      status: slate.pool.status || 'active',
      rulesConfig: slate.pool.rulesConfig
    };
  }, [slate]);

  // Get players filtered for a specific slot position
  const playersForSlot = useCallback((slotLabel: string): NormalizedPlayer[] => {
    const allowedPositions = SLOT_POSITIONS[slotLabel] || [];
    return players.filter(p => allowedPositions.includes(p.position || p.pos));
  }, [players]);

  return {
    players,
    playersForSlot,
    pool,
    loading,
    error
  };
}

export default useActiveSlate;
